import { FC, useCallback } from "react";
import { matchIsValidTel } from "mui-tel-input";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Routes } from "router";
import { Button, Checkbox, Typography } from "theme";

import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid2 } from "@mui/material";

import { InvertButton } from "../../../theme/components/Button/Button.tsx";
import PhoneInput from "../../../theme/components/TextField/PhoneInput.tsx";
import { useBooking } from "../../../utils/context/Booking/BookingContext.tsx";
import ControlledTextField from "../../ControlledInputs/ControlledTextField.tsx";

import CountSelect from "./components/CountSelect.tsx";
import HallSelect from "./components/HallSelect.tsx";
import LightSetUp from "./components/LightSetUp.tsx";
import SlotSelect from "./components/SlotSelect.tsx";
import TimeSelect from "./components/TimeSelect.tsx";
import { BookingInfoFormScheme, infoScheme } from "./validation.ts";

type BookingInfoFormProps = {
  setStep: (step: number) => void;
}

const BookingInfoForm: FC<BookingInfoFormProps> = ({ setStep }) => {
  const { setBooking, booking, setEquipment, equipment } = useBooking();
  const navigate = useNavigate();
  const { id } = useParams();
  const isNewBooking = id === "new";

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<BookingInfoFormScheme>({
    reValidateMode: "onSubmit",
    resolver: zodResolver(infoScheme),
    defaultValues: booking || {
      hall: 0,
      date: null,
      hoursNumber: 0,
      slotDate: "",
      lightSetup: "",
      name: "",
      phone: "",
      email: "",
      comment: "",
      sendBookingDetailsSms: true,
      smsText: ""
    },
  });

  const onSubmit = useCallback((data: BookingInfoFormScheme) => {
    setBooking(data);
    const updatedEquipment = equipment?.equipment.map(el => ({
      ...el,
      range: (el.range > data.hoursNumber || el.range === booking?.hoursNumber) ? data.hoursNumber : el.range
    }))
    if (updatedEquipment){
      setEquipment({
        equipment: updatedEquipment
      });
    }
    setStep(2);
  }, [booking?.hoursNumber, equipment?.equipment, setBooking, setEquipment, setStep]);

  const onHallSelectOptionsChange = useCallback(() => {
    setValue('date', null);
    setValue('hoursNumber', 0);
    setValue('slotDate', "");
  }, [setValue]);

  const onTimeSelectOptionsChange = useCallback(() => {
    setValue('hoursNumber', 0);
    setValue('slotDate', "");
  }, [setValue]);

  const onHoursSelectOptionsChange = useCallback(() => {
    setValue('slotDate', "");
  }, [setValue]);

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate my={3.5}>
      <Typography mb={4.5} variant="h1" textTransform="uppercase" weight="semiBold">
        Загальна інформація
      </Typography>

      <Grid2 mb={5} container columnGap="140px">
        <Box maxWidth="310px" width="100%">
          <Typography mb={2.5} variant="h2" textTransform="uppercase" weight="semiBold">
            Інформація про бронювання
          </Typography>

          <Box mb={2}>
            <HallSelect
              onOptionsChange={onHallSelectOptionsChange}
              label="Кімната"
              control={control}
              required
              name="hall"
            />
            {errors.hall?.message && (
              <Typography color="error" variant="body2">
                {errors.hall.message}
              </Typography>
            )}
          </Box>

          <Box mb={2}>
            <TimeSelect
              onOptionsChange={onTimeSelectOptionsChange}
              label="Дата та час"
              control={control}
              name="date"
            />
            {errors.date?.message && (
              <Typography color="error" variant="body2">
                {errors.date.message}
              </Typography>
            )}
          </Box>

          <Grid2 mb={2} container justifyContent="space-between" flexWrap="nowrap" columnGap={2.5}>
            <Box width="100%">
              <CountSelect
                onOptionsChange={onHoursSelectOptionsChange}
                label="Кількість годин"
                control={control}
                required
                name="hoursNumber"
              />
              {errors.hoursNumber?.message && (
                <Typography color="error" variant="body2">
                  {errors.hoursNumber.message}
                </Typography>
              )}
            </Box>

            <Box width="100%">
              <SlotSelect label="Час" control={control} required name="slotDate" />
              {errors.slotDate?.message && (
                <Typography color="error" variant="body2">
                  {errors.slotDate.message}
                </Typography>
              )}
            </Box>
          </Grid2>

          <Box mb={2}>
            <LightSetUp label="Набір світла" control={control} required name="lightSetup" />
            {errors.lightSetup?.message && (
              <Typography color="error" variant="body2">
                {errors.lightSetup.message}
              </Typography>
            )}
          </Box>

          {
            isNewBooking && <>
              <Grid2 mb={2} container columnGap={2}>
                <Checkbox
                  checked={watch("sendBookingDetailsSms")}
                  onChange={() => {
                    const newValue = !watch("sendBookingDetailsSms");
                    setValue("sendBookingDetailsSms", newValue);
                    if (!newValue) {
                      setValue('smsText', '')
                    }
                  }}
                />
                <Typography variant="body1">Надсилати SMS</Typography>
              </Grid2>
              <Box mb={2}>
                <ControlledTextField
                  disabled={!watch("sendBookingDetailsSms")}
                  control={control}
                  required
                  name="smsText"
                  label="Текст SMS(необов'язково)"
                  placeholder="Введіть текст SMS"
                  type="text"
                />
                {errors.smsText?.message && <Typography color="error" variant="body2">{errors.smsText.message}</Typography>}
              </Box>
            </>
          }
        </Box>

        <Box maxWidth="335px" width="100%">
          <Typography mb={2.5} variant="h2" textTransform="uppercase" weight="semiBold">
            Інформація про клієнта
          </Typography>

          <Box mb={2}>
            <ControlledTextField
              control={control}
              required
              name="name"
              label="Ім'я"
              placeholder="Введіть ваше ім'я"
              type="text"
            />
            {errors.name?.message && <Typography color="error" variant="body2">{errors.name.message}</Typography>}
          </Box>

          <Box mb={2}>
            <Controller
              name="phone"
              control={control}
              rules={{
                validate: (value) => matchIsValidTel(value),
              }}
              render={({ field: { ref: fieldRef, value, ...fieldProps }, fieldState }) => (
                <PhoneInput
                  fullWidth
                  {...fieldProps}
                  value={value ?? ""}
                  inputRef={fieldRef}
                  defaultCountry="UA"
                  label="Телефон"
                  placeholder="(__) ___ __ __"
                  onlyCountries={["UA"]}
                  helperText={fieldState.invalid ? "Невалідний номер" : ""}
                  error={fieldState.invalid}
                  forceCallingCode
                  disableDropdown
                  getFlagElement={() => null}
                />
              )}
            />
          </Box>

          <Box mb={2}>
            <ControlledTextField
              control={control}
              name="email"
              label="Email"
              placeholder="Введіть ваш Емейл"
              type="email"
            />
            {errors.email?.message && (
              <Typography color="error" variant="body2">
                {errors.email.message}
              </Typography>
            )}
          </Box>

          <Box>
            <ControlledTextField
              control={control}
              name="comment"
              placeholder="Введіть ваш коментар"
              label="Коментар"
              type="text"
              autoComplete="off"
              multiline
            />
          </Box>
        </Box>
      </Grid2>

      <Grid2 container justifyContent="space-between">
        <Button type="button" look="normal" onClick={() => navigate(Routes.Root)}>
          Скасувати
        </Button>

        <InvertButton type="submit">Продовжити</InvertButton>
      </Grid2>
    </Box>
  );
};

export default BookingInfoForm;
